import { call, put, takeEvery } from "redux-saga/effects";
import {
  ADVANCED_SEARCH,
  DETAIL_FOLDER_LIST,
  DOCUMENT_PAGES_LIST,
  DOCUMENT_PERMISSIONS,
  LIST_REQUIRE_SIGNATURE_DOCUMENT,
  LIST_REQUIRE_SIGNATURE_DOCUMENT_CLEAR_DATA,
  RESEND_SIGNATURE_REQUEST,
  RESEND_SIGNATURE_REQUEST_CLEAR_DATA,
  SEARCH_ARCHIVES_LIST,
  SEARCH_DOCUMENTS_LIST,
  DOCUMENT_VERSIONS,
  RESTORE_DOCUMENT_VERSION,
  RESTORE_DOCUMENT_VERSION_CLEAR_DATA,
  GET_BATCH_DOCUMENT,
  GET_BATCH_DOCUMENT_CLEAR_DATA,
} from "./actionTypes";
import {
  apiErrorDetailFolder,
  apiErrorDocumentPage,
  apiErrorSearchArchives,
  advancedSearchSuccess,
  apiErrorAdvancedSearch,
  detailFolderSuccess,
  documentPageSuccess,
  getDocumentPermissionsError,
  getDocumentPermissionsSuccess,
  getListRequireSignatureDocumentError,
  getListRequireSignatureDocumentSuccess,
  resendSignatureRequestError,
  resendSignatureRequestSuccess,
  searchArchivesSuccess,
  searchDocumentsSuccess,
  apiErrorSearchDocuments,
  getDocumentVersionsSuccess,
  getDocumentVersionsError,
  restoreDocumentVersionSuccess,
  restoreDocumentVersionError,
  restoreDocumentVersionClearData,
  getBatchDocumentSuccess,
  getBatchDocumentClearData,
  getBatchDocumentError,
} from "./actions";
import { toast } from "react-toastify";

import {
  getDetailFolder,
  getDocumentPermissionsAPI,
  getListRequireSignatureDocumentAPI,
  postDocumentPages,
  postAdvancedSearch,
  postSearchArchives,
  postSearchDocuments,
  resendSignatureRequestEmailAPI,
  getDocumentVersions,
  postRestoreDocumentVersion,
  getBatchProcessAPI
} from "../../helpers/call_api";

function* getDocumentPageList({
  payload: { documentPages, page, size, navigate },
}) {
  try {
    const response = yield call(
      postDocumentPages,
      documentPages.id,
      page,
      size
    );
    yield put(documentPageSuccess(response));
  } catch (error) {
    if (error.response.status) {
      if (error.response.status === 401) {
        navigate("/login");
      }
    }
    yield put(apiErrorDocumentPage(error));
    //DOC-1837 add messages on empty information and extension not supported
    if (
      error.response?.data?.error?.messages[0].message
        .toLowerCase()
        .includes("document doesn't exists")
    ) {
      toast.error("Document cannot be opened, reason: Document is empty.", {
        toastId: "document-empty",
      });
    } else if (
      error?.response?.data?.error?.messages[0].message
        .toLowerCase()
        .includes("extension not supported")
    ) {
      toast.error(
        "Document cannot be opened, reason: Extension is not supported.",
        { toastId: "extension-notsupported" }
      );
    }
  }
}

// Dedupe: skip same (parentArchiveId, page, pageSize) within this window to avoid multiple identical ByDetailFolderResource calls on home load
const DETAIL_FOLDER_DEDUPE_MS = 1200;
let _lastDetailFolderKey = null;
let _lastDetailFolderTime = 0;

function* getDetailFolderList({ payload: { detailFolder, navigate } }) {
  const key = `${detailFolder.parentArchiveId ?? ""}|${detailFolder.page}|${detailFolder.pageSize}`;
  const now = Date.now();
  if (_lastDetailFolderKey === key && now - _lastDetailFolderTime < DETAIL_FOLDER_DEDUPE_MS) {
    return; // duplicate request within window; skip to avoid 3 identical calls
  }
  _lastDetailFolderKey = key;
  _lastDetailFolderTime = now;

  try {
    const response = yield call(
      getDetailFolder,
      detailFolder.parentArchiveId,
      detailFolder.page,
      detailFolder.pageSize,
      detailFolder.cacheBust // Pass cache-busting timestamp if provided
    );
    yield put(detailFolderSuccess(response));
  } catch (error) {
    if (error?.response?.status) {
      if (error.response.status === 401) {
        navigate("/login");
      }
    }
    yield put(apiErrorDetailFolder(error));
  } finally {
    // Reset immediately so the next request (e.g. navigate back) is never skipped and the modal always closes. Tradeoff: sometimes 2 requests instead of 1.
    _lastDetailFolderKey = null;
    _lastDetailFolderTime = 0;
  }
}

function* getSearchArchivesList({ payload: { searchArchives, navigate } }) {
  try {
    const response = yield call(
      postSearchArchives,
      searchArchives.parentArchiveId,
      searchArchives.page,
      searchArchives.pageSize,
      searchArchives.obTerm,
      searchArchives.sortBy,
      searchArchives.sortOrder
    );
    yield put(searchArchivesSuccess(response));
  } catch (error) {
    if (error.response.status) {
      if (error.response.status === 401) {
        navigate("/login");
      }
    }
    yield put(apiErrorSearchArchives(error));
  }
}

// Full-text search with doctype and custom field filtering
function* getAdvancedSearch({ payload: { searchRequest, navigate } }) {
  try {
    const response = yield call(postAdvancedSearch, searchRequest);
    yield put(advancedSearchSuccess(response));
  } catch (error) {
    if (error?.response?.status) {
      if (error.response.status === 401) {
        navigate("/login");
      }
    }
    yield put(apiErrorAdvancedSearch(error));
  }
}

// Time-period based search with Redis caching (30-second TTL)
function* getSearchDocumentsList({ payload: { searchDocuments, navigate } }) {
  try {
    const response = yield call(
      postSearchDocuments,
      searchDocuments.userId,
      searchDocuments.parentArchiveId,
      searchDocuments.period,
      searchDocuments.page,
      searchDocuments.pageSize,
      searchDocuments.obTerm,
      searchDocuments.sortBy,
      searchDocuments.sortOrder
    );
    yield put(searchDocumentsSuccess(response));
  } catch (error) {
    if (error?.response?.status) {
      if (error.response.status === 401) {
        navigate("/login");
      }
    }
    yield put(apiErrorSearchDocuments(error));
  }
}

function* getDocumentPermissions({ payload: { id, navigate } }) {
  try {
    const response = yield call(getDocumentPermissionsAPI, id);

    yield put(getDocumentPermissionsSuccess(response));
  } catch (error) {
    if (error.response.status) {
      if (error.response.status === 401) {
        navigate("/login");
      }
    }
    yield put(getDocumentPermissionsError(error));
  }
}

function* getDocumentVersionsList({ payload: { id, navigate } }) {
  try {
    const response = yield call(getDocumentVersions, id);
    yield put(getDocumentVersionsSuccess(response));
  } catch (error) {
    if (error.response.status) {
      if (error.response.status === 401) {
        navigate("/login");
      }
    }
    yield put(getDocumentVersionsError(error));
  }
}

// List require signature for document
function* getListRequireSignatureDocument({ payload: { id } }) {
  try {
    const response = yield call(getListRequireSignatureDocumentAPI, `?DocumentId=${id}`);
    yield put(getListRequireSignatureDocumentSuccess(response));
  } catch (error) {
    yield put(getListRequireSignatureDocumentError(error));
  }
}

function* listRequireSignatureDocumentClearData(_action) {
  try {
    yield put(listRequireSignatureDocumentClearData(true))
  } catch (error) {
    yield put(getListRequireSignatureDocumentError(error))
  }
}
// END

// Resend Signature request email
function* resendSignatureRequest({ payload: { data } }) {
  try {
    const response = yield call(resendSignatureRequestEmailAPI, data.obj, data.documentId);
    yield put(resendSignatureRequestSuccess(response));
  } catch (error) {
    yield put(resendSignatureRequestError(error));
  }
}

function* resendSignatureRequestClearData() {
  try {
    yield put(listRequireSignatureDocumentClearData(true))
  } catch (error) {
    yield put(getListRequireSignatureDocumentError(error))
  }
}

function* restoreDocumentVersion({ payload: { id } }) {
  try {
    const response = yield call(postRestoreDocumentVersion, id);
    yield put(restoreDocumentVersionSuccess(response));
  } catch (error) {
    yield put(restoreDocumentVersionError(error));
  }
}

function* getBatchDocument({ payload: { id } }) {
  try {
    const response = yield call(getBatchProcessAPI, id);
    yield put(getBatchDocumentSuccess(response));
  } catch (error) {
    yield put(getBatchDocumentError(error));
  }
}
// END
function* authSaga() {
  // takeEvery so the first request can complete and put(detailFolderSuccess) and close the loading modal; dedupe above skips duplicate requests within 1.2s
  yield takeEvery(DETAIL_FOLDER_LIST, getDetailFolderList);
  yield takeEvery(ADVANCED_SEARCH, getAdvancedSearch);
  yield takeEvery(SEARCH_ARCHIVES_LIST, getSearchArchivesList);
  yield takeEvery(SEARCH_DOCUMENTS_LIST, getSearchDocumentsList);
  yield takeEvery(DOCUMENT_PAGES_LIST, getDocumentPageList);
  yield takeEvery(DOCUMENT_PERMISSIONS, getDocumentPermissions);
  yield takeEvery(LIST_REQUIRE_SIGNATURE_DOCUMENT, getListRequireSignatureDocument);
  yield takeEvery(LIST_REQUIRE_SIGNATURE_DOCUMENT_CLEAR_DATA, listRequireSignatureDocumentClearData);
  yield takeEvery(RESEND_SIGNATURE_REQUEST, resendSignatureRequest);
  yield takeEvery(RESEND_SIGNATURE_REQUEST_CLEAR_DATA, resendSignatureRequestClearData);
  yield takeEvery(DOCUMENT_VERSIONS, getDocumentVersionsList);
  yield takeEvery(RESTORE_DOCUMENT_VERSION, restoreDocumentVersion);
  yield takeEvery(RESTORE_DOCUMENT_VERSION_CLEAR_DATA, restoreDocumentVersionClearData);
  yield takeEvery(GET_BATCH_DOCUMENT, getBatchDocument);
  yield takeEvery(GET_BATCH_DOCUMENT_CLEAR_DATA, getBatchDocumentClearData);
}

export default authSaga;
